import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Check } from 'lucide-react';

import Badge from '../../../Badge.jsx';
import { toneFor } from '../constants/statTones';
import { colourForToken, formatValue, toneForToken, WIDGET_LOCALE } from '../shared';

/** The order severities are read in — worst first, regardless of key order. */
const SEVERITY_ORDER = Object.freeze(['DANGER', 'WARNING', 'INFO', 'SUCCESS']);

/**
 * What each severity is called on the row.
 *
 * `DANGER` reads as "Critical" rather than "Danger": the tone vocabulary the
 * platform shares calls that level critical everywhere else, and a widget that
 * renames it is a widget the reader has to translate.
 */
const SEVERITY_LABEL = Object.freeze({
  DANGER: 'Critical',
  WARNING: 'Warning',
  INFO: 'Info',
  SUCCESS: 'OK',
});

/** An alert nobody has to act on any more. */
const isAcknowledged = (status) => status === 'ACK' || status === 'ACKNOWLEDGED';

/**
 * Open problems, ranked by severity.
 *
 * Semantic colour throughout, unlike the charts — these genuinely *are*
 * states, which is the one case where reaching for red means something.
 *
 * ## Reading the contract (CT-5641)
 *
 * `ALERTS` publishes two things, and this used to render neither:
 *
 * - **`alerts[]`** — the individual alerts, each with its own `title`,
 *   `severity`, `status`, `message` and `created_at`. The previous shape was
 *   `rows[{label, count}]`, which could say "3 critical" but never *which
 *   three*. An alerts widget whose rows are counts is a stat row.
 * - **`counts_by_severity`** — the totals, which are still worth showing
 *   because the list is capped and "1 critical" has to be true even when the
 *   critical one is on page two.
 *
 * ## How a row is laid out, and why
 *
 * Severity is a **named chip at the left edge**, not just a coloured icon. The
 * list is sorted worst-first, so the reader's job is to find where the serious
 * ones stop — and that boundary is read by sweeping the left edge, which only
 * works if the level is a word in a fixed column. Hue alone fails the same
 * reader it is meant to serve.
 *
 * Then the title, then `message` on **its own line**. It is a sentence
 * ("3 sets left; weekly use 11."), and a sentence concatenated onto a
 * timestamp reads as neither.
 *
 * Last, muted: when it was raised and what state it is in. A `status` is not
 * the alert's subject, so it does not get the right edge — an acknowledged
 * alert is marked with a tick there instead, which is the only status worth
 * seeing from across the card.
 *
 * Zero counts are printed. `OK 0` means the check ran and found nothing, which
 * is not the same as a severity that was never evaluated — and dropping it
 * makes the strip's columns move between refreshes.
 *
 * @param {object} props
 * @param {{ alerts: object[], counts_by_severity?: object }} props.data
 * @param {(alert: object) => void} [props.onRowClick]
 * @param {string} [props.className]
 */
function AlertsWidget({ data, onRowClick, className = '' }) {
  const rows = useMemo(() => {
    if (data?.alerts) {
      return data.alerts.map((alert, index) => ({
        key: alert.row_key ?? alert.title ?? String(index),
        severity: alert.severity,
        label: alert.title,
        message: alert.message ?? '',
        meta: [
          alert.created_at ? formatValue(alert.created_at, { style: 'DATETIME' }) : '',
          alert.status ? alert.status.toLowerCase() : '',
        ].filter(Boolean).join(' · '),
        acknowledged: isAcknowledged(alert.status),
        tone: toneForToken(alert.severity),
        raw: alert,
      }));
    }
    // The pre-v2 shape: one row per severity, with the count as the figure.
    return (data?.rows ?? []).map((row) => ({
      key: row.label,
      severity: null,
      label: row.label,
      message: '',
      meta: '',
      trailing: row.count?.toLocaleString(WIDGET_LOCALE) ?? '',
      acknowledged: false,
      tone: row.tone ?? 'neutral',
      raw: row,
    }));
  }, [data]);

  const counts = useMemo(() => Object.entries(data?.counts_by_severity ?? {})
    .sort((a, b) => SEVERITY_ORDER.indexOf(a[0]) - SEVERITY_ORDER.indexOf(b[0]))
    .map(([severity, count]) => ({
      severity,
      count,
      label: SEVERITY_LABEL[severity] ?? severity,
      colour: colourForToken(severity),
    })), [data]);

  if (!rows.length) return null;

  return (
    <div className={`flex min-w-0 flex-col gap-2 ${className}`.trim()}>
      {counts.length ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {counts.map((entry) => (
            <span key={entry.severity} className="flex items-center gap-1.5">
              <span
                aria-hidden
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: entry.colour }}
              />
              <span className="text-[11px] text-content-muted">{entry.label}</span>
              <span className="text-[11px] font-semibold text-content">{entry.count}</span>
            </span>
          ))}
        </div>
      ) : null}

      <ul className="m-0 flex list-none flex-col p-0">
        {rows.map((row) => {
          const tone = toneFor(row.tone);
          const body = (
            <>
              {row.severity ? (
                <span className="shrink-0 pt-px">
                  <Badge size="small" variant={tone.badge}>
                    {SEVERITY_LABEL[row.severity] ?? row.severity}
                  </Badge>
                </span>
              ) : null}

              <span className="flex min-w-0 flex-1 flex-col gap-px">
                <span className="truncate text-xs font-medium text-content">{row.label}</span>
                {row.message ? (
                  <span className="truncate text-[11px] text-content-muted">{row.message}</span>
                ) : null}
                {row.meta ? (
                  <span className="truncate text-[10px] text-content-faint">{row.meta}</span>
                ) : null}
              </span>

              {row.acknowledged ? (
                <Check size={14} className="shrink-0 text-content-muted" aria-label="acknowledged" />
              ) : null}
              {row.trailing ? (
                <span className="shrink-0 text-[13px] font-semibold text-content">
                  {row.trailing}
                </span>
              ) : null}
            </>
          );

          return (
            <li
              key={row.key}
              className="flex items-start gap-2 border-b border-edge py-2 last:border-b-0"
            >
              {onRowClick ? (
                <button
                  type="button"
                  onClick={() => onRowClick(row.raw)}
                  className="flex min-w-0 flex-1 items-start gap-2 rounded text-left hover:text-content-hover"
                >
                  {body}
                </button>
              ) : (
                body
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

AlertsWidget.propTypes = {
  /** `ALERTS`' `alerts[]` + `counts_by_severity`. The pre-v2 `rows[]` still renders. */
  data: PropTypes.shape({
    alerts: PropTypes.arrayOf(
      PropTypes.shape({
        row_key: PropTypes.string,
        title: PropTypes.string,
        severity: PropTypes.oneOf(['INFO', 'SUCCESS', 'WARNING', 'DANGER']),
        status: PropTypes.string,
        message: PropTypes.string,
        created_at: PropTypes.string,
      }),
    ),
    counts_by_severity: PropTypes.object,
    /** The pre-v2 shape, still accepted. */
    rows: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onRowClick: PropTypes.func,
  className: PropTypes.string,
};

export default memo(AlertsWidget);
